import { useToggle } from 'ahooks';
import { ServiceLine } from 'api/types/service.types';
import type { TimeSlots } from 'api/types/timeSlot.types';
import { LightPlusIcon } from 'assets/icons/app';
import Badge from 'components/molecules/Badge';
import OptionItem from 'components/molecules/OptionItem';
import PremiumBadge from 'components/molecules/PremiumBadge';
import WhatToExpectBottomsheet from 'components/organisms/BottomSheets/WhatToExpectBottomSheet';
import CollectionMethodSelector from 'components/organisms/CollectionMethodSelector';
import { usePickupAndDropoff } from 'packages/shared/newOrder/hooks/usePickupAndDropoff';
import type { NewOrderModule } from 'packages/shared/newOrder/types';
import { selectIsMultipleServicesSelected } from 'pages/newOrder/newOrder.selectors';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectIsFirstTimeCustomer } from 'redux-stores/slices/authSlice';
import styled, { useTheme } from 'styled-components';

import TimeSlotSelector from '../TimeSlotSelector';

const StyledOptionItem = styled(OptionItem)`
  padding-top: 11px;
  padding-bottom: 11px;
`;

const StyledLightPlusIcon = styled(LightPlusIcon)`
  margin-top: 10px;
`;

const SelectPickupOrDropoff: React.FC<NewOrderModule.SelectPickupOrDropoffProps> = ({
  action = 'PICKUP',
  description,
  hasRightArrowOnly = false,
  serviceLine = ServiceLine.LAUNDRY_SERVICE,
  serviceLineDependancy,
  timeSlotsData,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [showGettingBagBottomsheet, { setRight, setLeft }] = useToggle(false);
  const isShownWhatToExpect = useRef(false);
  const isFirstTime = useSelector(selectIsFirstTimeCustomer);
  const theme = useTheme();
  const {
    activeOrdersCount,
    isPremiumTimeSlot,
    isUrgent,
    isDisabled,
    selectedTimeslot,
    isCollectionMethodAtDoor,
    handleCollectionMethodSelected,
    handleTimeslotSelectorSubmit,
  } = usePickupAndDropoff({
    action,
    serviceLine,
    serviceLineDependancy,
  });

  const hasMultipleServicesSelected = useSelector(selectIsMultipleServicesSelected);
  const [isTimeslotSelectorOpen, toggleTimeslotSelector] = React.useState(false);
  const [isCollectionMethodSelectorOpen, toggleCollectionMehtodSelector] = React.useState(false);

  const toggleCollectionMethodSheet = React.useCallback((value: boolean) => toggleCollectionMehtodSelector(value), []);

  const optionItemTitle = React.useMemo(() => {
    const collectionMethodT = isCollectionMethodAtDoor ? t('common.atDoor') : t('common.inPerson');
    switch (action) {
      case 'PICKUP':
        return selectedTimeslot ? `${t('common.pickUp')} ${collectionMethodT}` : t('newOrderPage.schedulePickUp');
      case 'DROPOFF':
        return selectedTimeslot
          ? `${t(`common.dropOff${hasMultipleServicesSelected ? serviceLine : ''}`)} ${collectionMethodT}`
          : t(`newOrderPage.scheduleDropOff${hasMultipleServicesSelected ? serviceLine : ''}`);
      default:
        return '';
    }
  }, [isCollectionMethodAtDoor, t, action, selectedTimeslot, hasMultipleServicesSelected, serviceLine]);

  const handleOptionItemClick = React.useCallback(async () => {
    if (action === 'PICKUP' && isFirstTime && activeOrdersCount === 0 && isShownWhatToExpect.current) {
      await handleCollectionMethodSelected(action, 'IN_PERSON');
      toggleTimeslotSelector(true);
    } else if (action === 'PICKUP' && isFirstTime && !isShownWhatToExpect.current) {
      setRight();
      isShownWhatToExpect.current = true;
    } else {
      toggleCollectionMethodSheet(true);
    }
  }, [action, activeOrdersCount, handleCollectionMethodSelected, isFirstTime, setRight, toggleCollectionMethodSheet]);

  const onTimeSlotSelected = useCallback(
    (pickedTimeslot: TimeSlots.Timeslot) => {
      handleTimeslotSelectorSubmit(pickedTimeslot);
      toggleTimeslotSelector(false);
    },
    [handleTimeslotSelectorSubmit]
  );

  const handleCollectionMethodSelectorClose = async (selectedCollectionMethod?: string) => {
    toggleCollectionMehtodSelector(false);
    if (selectedCollectionMethod) {
      await handleCollectionMethodSelected(action, selectedCollectionMethod);
      toggleTimeslotSelector(true);
    }
  };

  const onCloseExpectSheet = useCallback(() => {
    setLeft();
    handleOptionItemClick();
  }, [handleOptionItemClick, setLeft]);

  const Footer = useMemo(
    () =>
      action === 'DROPOFF' ? (
        <div className="d-flex">
          {isUrgent && <Badge className="mr-4" content={`+${t('newOrderPage.surcharge50')}`} />}
          {isPremiumTimeSlot && <PremiumBadge />}
        </div>
      ) : null,
    [action, isPremiumTimeSlot, isUrgent, t]
  );

  const isShowRightButtonOnUnselected = useMemo(
    () => theme.components.selectPickupOrDropOffItem?.showUnselectedRightButton && !selectedTimeslot?.selectedDateTimeText,
    [theme.components.selectPickupOrDropOffItem?.showUnselectedRightButton, selectedTimeslot?.selectedDateTimeText]
  );

  const rightIcon = useMemo(() => (isShowRightButtonOnUnselected ? <StyledLightPlusIcon /> : undefined), [isShowRightButtonOnUnselected]);

  return (
    <>
      <StyledOptionItem
        onClick={handleOptionItemClick}
        startIconName={action === 'PICKUP' ? 'icon-pickup' : 'icon-dropoff'}
        title={optionItemTitle}
        actionType={
          hasRightArrowOnly || isShowRightButtonOnUnselected
            ? theme.components.selectPickupOrDropOffItem?.icon.unselected
            : theme.components.selectPickupOrDropOffItem?.icon.selected
        }
        hasRightArrowOnly={hasRightArrowOnly || isShowRightButtonOnUnselected}
        hideItemSeparator
        value={selectedTimeslot?.selectedDateTimeText}
        rightIcon={rightIcon}
        isEditable
        disabled={isDisabled}
        description={description}
        footer={Footer}
      />
      {timeSlotsData && (
        <TimeSlotSelector
          show={isTimeslotSelectorOpen}
          onSubmit={onTimeSlotSelected}
          onClose={() => toggleTimeslotSelector(false)}
          action={action}
          timeslotsData={timeSlotsData}
          isAtDoor={isCollectionMethodAtDoor}
          isShowServiceTitle={hasMultipleServicesSelected && action === 'DROPOFF'}
          serviceLine={serviceLine}
        />
      )}
      <CollectionMethodSelector
        show={isCollectionMethodSelectorOpen}
        allowChange={!!timeSlotsData?.collectionMethod.allowChange}
        description={timeSlotsData?.collectionMethod.description}
        atDoorDescription={timeSlotsData?.collectionMethod.atDoorDescription}
        inPersonDescription={timeSlotsData?.collectionMethod.inPersonDescription}
        collectionMethod={isCollectionMethodAtDoor ? 'AT_DOOR' : 'IN_PERSON'}
        action={action}
        onClose={handleCollectionMethodSelectorClose}
        isLoading={isLoading}
        serviceLine={serviceLine}
        isShowTitle={hasMultipleServicesSelected && action === 'DROPOFF'}
      />
      <WhatToExpectBottomsheet show={showGettingBagBottomsheet} onClose={onCloseExpectSheet} />
    </>
  );
};

export default SelectPickupOrDropoff;
